import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  type ToggleButtonGroupProps,
  Typography,
} from "@mui/material";
import { forwardRef } from "react";
import type { Merge } from "type-fest";

export type ToggleOptionType = {
  label: string;
  value: string | number | boolean;
};

export type BaseToggleProps = Merge<
  ToggleButtonGroupProps,
  {
    options: ToggleOptionType[];
    error?: boolean;
    helperText?: string;
    onChange: (value: string | number | boolean) => void;
  }
>;

export const BaseToggle = forwardRef<HTMLDivElement, BaseToggleProps>(
  ({ value, options = [], sx, onChange, error, helperText, ...props }, ref) => {
    return (
      <Stack ref={ref} spacing={0.5}>
        <ToggleButtonGroup
          value={value}
          exclusive
          aria-label="Toggle Buttons"
          sx={{
            ...sx,
            backgroundColor: error ? "#FEF4F2" : "white",
            boarderColor: error ? "#EA583B" : "#D0D4D3",
            "& .MuiToggleButton-root:first-of-type": {
              borderRadius: "12px 0 0 12px", // 左端のボタン
            },
            "& .MuiToggleButton-root:last-of-type": {
              borderRadius: "0 12px 12px 0", // 右端のボタン
            },
            "& .MuiToggleButton-root.Mui-selected": {
              backgroundColor: error
                ? "#EA583B"
                : props.disabled
                  ? "grey.100"
                  : "primary.main",
              color: props.disabled ? "grey.500" : "white",
              ":hover": {
                backgroundColor: error
                  ? "#EA583B"
                  : props.disabled
                    ? "grey.700"
                    : "#3C8F76",
              },
            },
            "& .MuiOutlinedInput-root": { borderRadius: "12px" },
            "& .Mui-error.MuiOutlinedInput-root": {
              color: "#EA583B",
              bgcolor: "#EA583B",
            },
            "& .Mui-error": { bgcolor: "#EA583B" },
          }}
          {...props}
        >
          {options.map((option) => (
            <ToggleButton
              key={option.label}
              value={option.value}
              onClick={() => {
                onChange(option.value);
              }}
            >
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: error ? "error.main" : "grey.500",
            }}
          >
            {helperText}
          </Typography>
        )}
      </Stack>
    );
  },
);

BaseToggle.displayName = "BaseToggle";
